import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyLoginLink } from '../services/api';
import { getAuthToken } from '../services/auth';
import { useToast } from '../components/ToastContainer';
import ToastContainer from '../components/ToastContainer';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import AnimatedBackground from '../components/AnimatedBackground';
import { t, getLanguage } from '../utils/i18n';
import logoImage from '../assets/babysit_logo.png';

export default function EmailLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error', 'redirecting'
  const [, setLang] = useState(getLanguage());
  const { showToast, removeToast, toasts } = useToast();
  const processedRef = useRef(false);

  useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  useEffect(() => {
    if (processedRef.current) return;

    const verifyOnWeb = async (email, emailHash, token) => {
      try {
        setStatus('processing');
        if (import.meta.env.DEV) {
          console.log('Verifying login link on web...');
        }
        
        const result = await verifyLoginLink(email, token, emailHash);
        
        if (result.success) {
          setStatus('success');
          showToast(t('auth.login.loginSuccess'), 'success', 2000);
          const targetRoute = result.user?.profileComplete === false ? '/complete-profile' : '/dashboard';
          
          // Verify that token is saved before redirecting
          const checkToken = () => {
            const token = getAuthToken();
            if (token) {
              setStatus('redirecting');
              // Use navigate to ensure React Router handles the redirect properly
              navigate(targetRoute, { replace: true });
            } else {
              // If token not found, wait a bit more and try again (max 5 attempts)
              setTimeout(checkToken, 200);
            }
          };
          
          // Wait a bit for localStorage to be updated, then check and redirect
          setTimeout(checkToken, 500);
        } else {
          setStatus('error');
          showToast(result.error || t('auth.login.invalidLink'), 'error', 5000);
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        showToast(t('auth.login.verificationError'), 'error', 5000);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };
    
    const processLoginLink = async () => {
      if (processedRef.current) return;
      processedRef.current = true;
      
      try {
        // Get login_link from URL
        const loginLink = searchParams.get('login_link');
        
        if (!loginLink) {
          setStatus('error');
          showToast(t('auth.login.invalidLinkRetry'), 'error', 5000);
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        if (import.meta.env.DEV) {
          console.log('Processing login link:', loginLink);
        }

        // Parse deep link: com.mnocompany.babysittertracker://login?email=...&email_hash=...&token=...
        // Decode URL-encoded login link
        const decodedLink = decodeURIComponent(loginLink);
        if (import.meta.env.DEV) {
          console.log('Decoded link:', decodedLink);
        }
        
        // Try to extract parameters from deep link
        let email = null;
        let emailHash = null;
        let token = null;
        
        // Use regex to extract parameters
        const emailMatch = decodedLink.match(/email=([^&]+)/);
        const hashMatch = decodedLink.match(/email_hash=([^&]+)/);
        const tokenMatch = decodedLink.match(/token=([^&]+)/);
        
        if (emailMatch) email = decodeURIComponent(emailMatch[1]);
        if (hashMatch) emailHash = decodeURIComponent(hashMatch[1]);
        if (tokenMatch) token = decodeURIComponent(tokenMatch[1]);
        
        if (!email || !emailHash || !token) {
          setStatus('error');
          showToast(t('auth.login.invalidLinkCheckEmail'), 'error', 5000);
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }
        
        if (import.meta.env.DEV) {
          console.log('Extracted params:', { email, emailHash: emailHash.substring(0, 10) + '...', token: token.substring(0, 10) + '...' });
        }
        
        // Always verify on web (web login flow)
        await verifyOnWeb(email, emailHash, token);
      } catch (error) {
        console.error('Error processing login link:', error);
        setStatus('error');
        showToast(t('auth.login.processingError'), 'error', 5000);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } finally {
        setIsLoading(false);
      }
    };

    processLoginLink();
    
    // Cleanup function
    return () => {
      processedRef.current = true;
    };
  }, [searchParams, showToast, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
      <AnimatedBackground />
      {/* Background - subtle waves with radial gradient */}
      <div 
        className="absolute inset-0 opacity-30 dark:opacity-15 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(232, 90, 156, 0.30) 0%, transparent 55%),
            radial-gradient(circle at 80% 70%, rgba(232, 90, 156, 0.22) 0%, transparent 55%),
            radial-gradient(circle at 50% 50%, rgba(245, 158, 11, 0.10) 0%, transparent 60%)
          `,
          backgroundSize: '100% 100%',
        }}
      ></div>
      
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="card max-w-md w-full text-center">
          <img 
            src={logoImage} 
            alt="Nannity Logo" 
            className="h-16 sm:h-20 w-auto mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">
            Prijava...
          </h2>
          
          {isLoading || status === 'processing' ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                Obrada login linka...
              </p>
            </div>
          ) : status === 'success' || status === 'redirecting' ? (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full h-12 w-12 bg-green-100 dark:bg-green-900 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                {t('auth.login.redirecting')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full h-12 w-12 bg-red-100 dark:bg-red-900 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300">
                {t('auth.login.redirectingError')}
              </p>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

